import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  SurveySelector,
  type SurveySelectorProps,
} from '~/components/features/SurveySelector'
import { type Survey } from '~/hooks/useBrandAccountSurveysQuery'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useCurrentBrandAccountSurveysQueryData } from '~/hooks/useCurrentBrandAccountSurveysQuery'
import { useCurrentPrimarySurvey } from '~/hooks/useCurrentPrimarySurvey'
import { compact, exists } from '~/utils'

import styles from './NavigationSurveySelector.css'

export const NavigationSurveySelector = () => {
  const { t } = useTranslation()
  const currentPrimarySurvey = useCurrentPrimarySurvey()

  const currentBrandAccountAvailableSurveys =
    useCurrentBrandAccountSurveysQueryData()
  const currentBrandAccount = useCommonQueryCurrentBrandAccount()
  const newSurvey = React.useMemo(
    () => ({ title: t('create_new') }) as Survey,
    [t],
  )
  const surveySelectorItems = React.useMemo<Survey[]>(
    () =>
      compact([
        ...currentBrandAccountAvailableSurveys,
        currentBrandAccount?.isSurveyEditor && newSurvey,
      ]),
    [currentBrandAccount, currentBrandAccountAvailableSurveys, newSurvey],
  )

  const navigate = useNavigate()
  const handleSurveySelectorChange = React.useCallback<
    EH<SurveySelectorProps<Survey>, 'onChange'>
  >(
    (surveyId) => {
      if (exists(surveyId)) {
        navigate(compile('survey', { surveyId }))

        return
      }

      if (exists(currentBrandAccount) && currentBrandAccount.isSurveyEditor) {
        navigate(
          compile('createSurvey', { brandAccountId: currentBrandAccount.id }),
        )
      }
    },
    [currentBrandAccount, navigate],
  )

  return (
    <Box className={styles.root}>
      <LabelingContainer label={t('survey')} labelVariant="small">
        <SurveySelector
          className={styles.itemSelector}
          surveys={surveySelectorItems}
          value={currentPrimarySurvey ? currentPrimarySurvey.id : null}
          onChange={handleSurveySelectorChange}
        />
      </LabelingContainer>
    </Box>
  )
}
